import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { EASE } from "@/lib/design";
import { fetchMe, prefetchAll } from "@/lib/me";

// Séquence d'ouverture : logo → phrase → visage scanné → score d'attirance → question.
const Home = () => {
    const navigate = useNavigate();
    const reduce = useReducedMotion();
    const [phase, setPhase] = useState(0); // 0 logo · 1 phrase · 2 visage · 3 sortie
    const [score, setScore] = useState(0);
    const done = useRef(false);

    // Routage déterministe basé sur la source de vérité serveur (jamais le localStorage).
    const go = async () => {
        if (done.current) return;
        done.current = true;
        const me = await prefetchAll();
        if (me?.mode_createur) navigate("/createur");
        else if (me && me.questionnaire_termine) navigate("/accueil");
        else navigate("/analyse");
    };

    useEffect(() => {
        if (reduce) {
            setPhase(2); setScore(72);
            const t = setTimeout(go, 1200);
            return () => clearTimeout(t);
        }
        const timers = [
            setTimeout(() => setPhase(1), 900),
            setTimeout(() => setPhase(2), 1800),
            setTimeout(() => setPhase(3), 4600),
            setTimeout(go, 5100),
        ];
        return () => timers.forEach(clearTimeout);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Le score monte de 0 à 72 une fois que la ligne de scan a fini de passer (~1,4 s).
    useEffect(() => {
        if (phase < 2) return undefined;
        const start = setTimeout(() => {
            const t0 = Date.now();
            const id = setInterval(() => {
                const p = Math.min(1, (Date.now() - t0) / 900);
                setScore(Math.round(p * 72));
                if (p >= 1) clearInterval(id);
            }, 30);
        }, 1500);
        return () => clearTimeout(start);
    }, [phase]);

    const showFace = phase >= 2;
    const exiting = phase >= 3;

    return (
        <div
            data-testid="intro-screen"
            onClick={go}
            className="fixed inset-0 bg-black max-w-[480px] mx-auto overflow-hidden flex flex-col items-center justify-center"
        >
            <motion.div
                className="flex flex-col items-center"
                animate={exiting ? { opacity: 0, y: -40 } : { opacity: 1, y: 0 }}
                transition={{ duration: 0.42, ease: EASE }}
            >
                <motion.img
                    src="/brand/logo-glowup.png"
                    alt="Glow Up IA"
                    initial={{ opacity: 0, scale: 1.06 }}
                    animate={{ opacity: 1, scale: 1, width: showFace ? 150 : 230, y: showFace ? -14 : 0 }}
                    transition={{ duration: 0.9, ease: EASE }}
                    style={{ width: 230, height: "auto" }}
                />

                <motion.p
                    data-testid="intro-tagline"
                    className="font-display text-center"
                    style={{ fontSize: "40px", fontWeight: 700, lineHeight: 1.05, color: "#FFFFFF", maxWidth: 320, marginTop: 16 }}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: phase >= 1 ? 1 : 0, marginTop: showFace ? 10 : 16 }}
                    transition={{ duration: 0.5, ease: EASE }}
                >
                    Découvre ton score d'attirance.
                </motion.p>

                <motion.div
                    className="relative"
                    initial={{ opacity: 0, height: 0, marginTop: 0 }}
                    animate={{ opacity: showFace ? 1 : 0, height: showFace ? 300 : 0, width: 232, marginTop: showFace ? 28 : 0 }}
                    transition={{ duration: 0.6, ease: EASE }}
                >
                    {showFace ? (
                        <>
                            <img src="/brand/intro-visage.webp" alt="" className="absolute inset-0 w-full h-full object-cover rounded-2xl" />
                            <div className="absolute inset-0 rounded-2xl" style={{ boxShadow: "inset 0 0 40px 12px #000000" }} />
                            <motion.div
                                className="absolute left-0 right-0"
                                style={{ height: "1px", backgroundColor: "var(--red)" }}
                                initial={{ top: "0%" }} animate={{ top: "100%" }}
                                transition={{ duration: 1.4, ease: "linear" }}
                            />
                        </>
                    ) : null}
                </motion.div>

                {/* Score d'attirance, centré sous la photo */}
                {showFace ? (
                    <motion.div
                        className="flex flex-col items-center"
                        style={{ marginTop: 16 }}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.4, delay: 1.5, ease: EASE }}
                    >
                        <span data-testid="intro-score" className="font-display" style={{ fontSize: 48, fontWeight: 800, lineHeight: 1, color: "var(--red)" }}>
                            {score}
                        </span>
                        <span className="font-body" style={{ fontSize: 13, color: "var(--text-secondary)", marginTop: 4 }}>Score d'attirance</span>
                    </motion.div>
                ) : null}
            </motion.div>
        </div>
    );
};

export default Home;
